import { getContrastRatio, useTheme } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import MvTypographyProps from '../types/mvTypography.types'
import { getComputedStyleBackgroundColor, getTextColor, rgbToHex, StyledTypography } from '../utils'

const MvTypography: React.FC<MvTypographyProps> = ({
  typeSize = 'PX',
  size = 'BODY_MD_NORMAL',
  children,
  ...props
}) => {
  const theme = useTheme()
  const [textColor, setTextColor] = useState<string>('')
  const ref = useRef<HTMLDivElement>(null)

  function updateTextColor() {
    if (!ref.current) return

    const parentElement = ref.current.parentElement
    if (!parentElement) return

    const parentColor = getComputedStyleBackgroundColor(parentElement)
    const rgbValues = parentColor.match(/\d+/g)

    if (rgbValues) {
      const [r, g, b] = rgbValues.map(Number)
      const hexColor = rgbToHex(r, g, b).toUpperCase()
      const contrastRatio = getContrastRatio(`rgb(${r},${g},${b})`, '#ffffff')
      const textColorFromTheme = getTextColor(hexColor, theme)

      if (textColorFromTheme) {
        setTextColor(textColorFromTheme)
      } else {
        if (hexColor === theme?.colorToken?.background?.neutral?.normal) {
          setTextColor(theme?.colorToken?.text?.neutral?.normal)
        } else if (hexColor === theme?.colorToken?.background?.neutral?.subtle) {
          setTextColor(theme?.colorToken?.text?.neutral?.normal)
        } else if (hexColor === theme?.colorToken?.background?.neutralInverted?.normal) {
          setTextColor(theme?.colorToken?.text?.neutral?.inverted || theme?.colorToken?.text?.neutralInverted?.normal)
        } else {
          if (contrastRatio < 5.5) {
            if (contrastRatio < 2) {
              setTextColor(theme?.colorToken?.text?.neutral?.normal)
            } else {
              setTextColor(theme?.colorToken?.text?.neutral?.inverted || theme?.colorToken?.background?.neutral?.normal)
            }
          } else {
            setTextColor(theme?.colorToken?.text?.neutral?.normal)
          }
        }
      }
    } else {
      if (!parentColor) {
        setTextColor(theme?.colorToken?.text?.neutral?.normal)
        return
      }

      setTextColor(theme.palette.getContrastText(parentColor))
    }
  }

  useEffect(() => {
    if (!ref.current) return

    updateTextColor()

    // Create observer
    const observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (
          mutation.type === 'attributes' &&
          (mutation.attributeName === 'style' || mutation.attributeName === 'class')
        ) {
          updateTextColor()
        }
      })
    })

    const parentElement = ref.current.parentElement
    if (parentElement) {
      observer.observe(parentElement, {
        attributes: true,
        attributeFilter: ['style', 'class']
      })
    }

    // Cleanup observer
    return () => {
      observer.disconnect()
    }
  }, [theme.palette.mode])

  if (props.hidden) {
    return null
  }

  return (
    <StyledTypography ref={ref} typeSize={typeSize} size={size} color={props.color || textColor} {...(props as any)}>
      {children}
    </StyledTypography>
  )
}
export default MvTypography
